import { _animate } from "../utils/_animate";
import { _backgroundGradient } from "../utils/_backgroundGradient";
import { _lazyLoad } from "../utils/_lazyLoad";
import { _parallax, _parallaxScroller } from "../utils/_parallax";
import { _rollingNumber } from "../utils/_rollingNumber";
import MainClass, { MAIN_CLASS_PARAMS } from "../utils/mainClass";

class Testimonials extends MainClass {
    el: {
        header: HTMLElement | null;
        footer: HTMLElement | null;
        pagination: HTMLElement | null;
        items: NodeListOf<HTMLElement> | null;
        banners: NodeListOf<HTMLElement> | null;
    };

    background: _backgroundGradient | null;
    pagination: null;

    constructor(props: MAIN_CLASS_PARAMS) {
        super(props);

        this.el = {
            header: this.selector.querySelector(".js-testimonials-header"),
            footer: this.selector.querySelector(".js-testimonials-footer"),
            pagination: this.selector.querySelector(".js-testimonials-pagination"),
            items: this.selector.querySelectorAll(".js-testimonials-item"),
            banners: this.selector.querySelectorAll(".js-testimonials-banners"),
        };

        this.background = null;
        this.pagination = null;

        this.init();
    }

    init() {
        // for lazyload
        _lazyLoad();

        // for animate
        new _animate();

        // for styles
        this.style();

        // for bg
        this.background = new _backgroundGradient({
            target: this.selector,
            uniforms: {
                color1: "#ffffff", // bg
                color2: "#F6FBC6", // left edge
                color3: "#2F6DFF", // left core
                color4: "#2F6DFF", // right core
                color5: "#F6FBC6", // right edge
                // colorRotation: 0.5,
            },
            events: {
                init: (instance) => {
                    // instance.app.view.style.display = "none";
                    const _bg = document.createElement("div");
                    _bg.classList.add("absolute", "top-0", "left-0", "w-full", "h-full");
                    this.selector.prepend(_bg);

                    _bg.appendChild(instance.app.view);
                    instance.app.view.classList.remove("absolute", "h-full");
                    instance.app.view.classList.add("sticky", "h-screen");
                },
            },
        });

        let _currentTesti = null;

        // for pagination
        this.el.pagination.innerHTML = `<span>/ ${this.el.items.length}</span>`;

        const _number = document.createElement("div");
        this.el.pagination.prepend(_number);
        this.pagination = new _rollingNumber({ target: _number, duration: 400 });
        const _updatePagination = (index) => {
            if (this.pagination) this.pagination.roll({ to: index + 1 });
        };

        // for show/hide the testi items
        const _showTesti = (index, direction) => {
            // console.log("_showTesti", index);
            if (index === _currentTesti) return;

            const _item = this.el.items[index];

            _item.classList.remove("opacity-0");

            _item.style.opacity = 1;
            _item.style.zIndex = 1;

            const _animateItem = _item.querySelectorAll(".js-testimonials-item-animate");

            _animateItem.forEach((item, index) => {
                item.style.setProperty("--animate-delay", `${0.1 * index}s`);
                item.classList.add("js-testi-animate");
            });

            _animateItem.forEach((item) => {
                item.classList.remove("animate-fadeOutUp", "animate-fadeOutDown");
                item.classList.add(direction === "up" ? "animate-fadeInDown" : "animate-fadeInUp");
            });

            // for bg color
            if (this.background) {
                let _colors = _item.dataset.colors ? _item.dataset.colors.split(",") : [];
                this.background.update({
                    color1: _colors[0] || this.background.defaults?.color1,
                    color2: _colors[1] || this.background.defaults?.color2,
                    color3: _colors[2] || this.background.defaults?.color3,
                    color4: _colors[3] || this.background.defaults?.color4,
                    color5: _colors[4] || this.background.defaults?.color5,
                });
            }

            _updatePagination(index);
        };

        const _hideTesti = (index, direction) => {
            // console.log("_hideTesti", index);
            if (index === null) return;

            const _item = this.el.items[index];

            if (!_item) return;

            _item.style.zIndex = 0;

            const _animateItem = _item.querySelectorAll(".js-testimonials-item-animate");

            _animateItem.forEach((item, index) => {
                item.style.setProperty("--animate-delay", `0s`);
                item.classList.add("js-testi-animate");
            });

            _animateItem.forEach((item) => {
                item.classList.remove("animate-fadeInUp", "animate-fadeInDown");
                item.classList.add(direction === "down" ? "animate-fadeOutUp" : "animate-fadeOutDown");
            });
        };

        new _parallaxScroller({
            target: this.selector,
            init: (scroller) => {
                // hide all testi
                this.el.items.forEach((item) => {
                    item.style.opacity = 0;

                    item.querySelectorAll("*").forEach((item) => {
                        if (item.innerText && item.innerText.length > 0)
                            item.parentNode.classList.add("js-testimonials-item-animate");
                    });

                    item.querySelectorAll(".js-testimonials-item-animate").forEach((item) => {
                        Array.from(item.children).forEach((item) => {
                            if (["i", "strong", "span"].find((tag) => tag === item.tagName.toLowerCase())) return;
                            item.parentNode.classList.remove("js-testimonials-item-animate");
                            item.classList.add("js-testimonials-item-animate");
                        });
                    });
                });

                this.el.banners.forEach((item, index) => {
                    if (index === 0) item.style.setProperty("--offset", "36vh");
                });
            },
            resize: (scroller) => {
                this.selector.style.minHeight = `${100 + this.el.items.length * 50}vh`;
                const _offset = _parallax.getOffset(scroller.target);

                scroller.offset.top = _offset.top - window.innerHeight * 0.5;
                scroller.offset.bottom = _offset.bottom - window.innerHeight;

                this.selector.style.setProperty("--height", "100vh");
                this.selector.style.setProperty("--offset-top", `${this.el.header.offsetHeight}px`);
                this.selector.style.setProperty("--offset-bottom", `${this.el.footer.offsetHeight}px`);
            },
            update: (scroller) => {
                // for horizontal lines
                const _lineProgress = _parallax.abs(
                    scroller.offset.portion(scroller.offset.top, scroller.offset.top + window.innerHeight * 0.5),
                );
                this.selector.style.setProperty("--line-height", `${_lineProgress * 100}%`);

                // for current testimonial
                const _total = this.el.items.length;
                const _progress = scroller.offset.current;
                const _currentIndex = Math.min(Math.floor(_progress * _total), _total - 1);

                this.el.items.forEach((item, index) => {
                    if (index === _currentIndex) _showTesti(index, scroller.direction);
                });

                if (_currentIndex !== _currentTesti) _hideTesti(_currentTesti, scroller.direction);

                _currentTesti = _currentIndex;
            },
        });

        // console.log("Testimonials", this);
    }

    style() {
        let _style = document.querySelector(".testimonial-styles");
        if (!_style) {
            _style = document.createElement("style");
            document.head.appendChild(_style);

            _style.innerHTML =
                `.js-testi-animate { opacity: 0; animation-duration: 0.5s; animation-delay: var(--animate-delay, 0s); animation-timing-function: ease; animation-fill-mode: forwards; }` +
                `@keyframes testiFadeOutUp { 0% { opacity: 1; transform: translate3d(0,0,0); } 100% { opacity: 0; transform: translate3d(0,-24px,0); } } ` +
                `@keyframes testiFadeOutDown { 0% { opacity: 1; transform: translate3d(0,0,0); } 100% { opacity: 0; transform: translate3d(0,24px,0); } } ` +
                `@keyframes testiFadeInUp { 0% { opacity: 0; transform: translate3d(0,24px,0); } 100% { opacity: 1; transform: translate3d(0,0,0); } } ` +
                `@keyframes testiFadeInDown { 0% { opacity: 0; transform: translate3d(0,-24px,0); } 100% { opacity: 1; transform: translate3d(0,0,0); } } ` +
                `.js-testi-animate.animate-fadeOutUp { animation-name: testiFadeOutUp; }` +
                `.js-testi-animate.animate-fadeOutDown { animation-name: testiFadeOutDown; }` +
                `.js-testi-animate.animate-fadeInUp { animation-name: testiFadeInUp; }` +
                `.js-testi-animate.animate-fadeInDown { animation-name: testiFadeInDown; }`;
        }
    }
}

export default Testimonials;
